import { FeatureFlags } from "../../configs/FeatureFlags";
import {
  IPropertyFormDataType,
  PropertyFactFormTriState,
} from "../forms/FormDataTypes";
import { createLogger } from "../Logger";
import { IAutoComplianceRequest } from "../zeos-api/contracts/AutoCompliance";
import {
  IPropertyContract,
  PropertyTriStateFact,
} from "../zeos-api/contracts/Property";
import {
  CarbonClearGovEnum,
  CarbonClearTestEnum,
} from "../zeos-api/enums/AutoCompliance";
import {
  DocumentCategoryEnum,
  EntityTypeEnum,
} from "../zeos-api/enums/ListUtility";
import { IZeosApiService } from "../zeos-api/IZeosApiService";
import { resolveDocumentType } from "../zeos-api/ZeosApiHelpers";

/**
 * A matching fact on its way to the API (IMPL-002 task F).
 *
 * The three-way distinction is load-bearing and easy to lose: a form that does
 * not carry the field at all must send nothing, or saving an unrelated screen
 * would silently erase a fact the owner had answered.
 *
 *   undefined -> undefined   this form has no opinion; leave the stored fact
 *   ""        -> "Unknown"   the owner chose the blank option; clear the fact
 *   "Yes"/"No"               the owner answered
 */
export const triStateFactToContract = (
  value: PropertyFactFormTriState | undefined
): PropertyTriStateFact | undefined => {
  if (value === undefined) {
    return undefined;
  }
  return value === "" ? "Unknown" : value;
};

/**
 * Form value -> the boolean|null a screening intake carries. "" is unanswered,
 * which is not No: the engine leaves an unanswered fact flagged for
 * verification rather than excluding the programme.
 */
export const triStateFactToBoolean = (
  value: PropertyFactFormTriState | undefined
): boolean | null => {
  if (value === "Yes") {
    return true;
  }
  if (value === "No") {
    return false;
  }
  return null;
};

/** Inverse of the above, for seeding a form from a saved property. */
export const triStateFactToForm = (
  value: PropertyTriStateFact | undefined
): PropertyFactFormTriState => {
  if (value === true || value === "Yes") {
    return "Yes";
  }
  if (value === false || value === "No") {
    return "No";
  }
  return "";
};

/**
 * A numeric form field on its way to the API.
 *
 * These arrive as free text, and `Number("40,000")` is NaN, which
 * `JSON.stringify` writes as `null` -- so a square footage typed with the
 * thousands separator people naturally use was reaching the API as "clear this
 * column" and the value vanished without an error (bug 4044).
 *
 * Separators and surrounding space are stripped; anything still not a plain
 * number returns undefined, which omits the field rather than nulling it. The
 * form's validation rejects that input before a save gets here, so undefined
 * is the belt-and-braces case, not the expected one.
 */
export const numericFieldToContract = (
  value: string | number | undefined | null
): number | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  const cleaned = String(value).replace(/,/g, "").trim();
  if (cleaned === "") {
    return undefined;
  }
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Maps property form data to property contract payload.
 * Legacy `buildingTypeId` is passed through unchanged (UI no longer edits it).
 */
export const mapPropertyFormToContract = (
  formData: IPropertyFormDataType,
  companyId: number,
  propertyId?: number
): IPropertyContract => {
  const mixedUseComponents = formData.mixedUseComponents
    ?.filter((c) => c.detailId != null && Number(c.detailId) > 0)
    .map((c) => ({
      detailId: Number(c.detailId),
      allocationPercent:
        c.allocationPercent != null ? Number(c.allocationPercent) : undefined,
      allocationSqFt:
        c.allocationSqFt != null ? Number(c.allocationSqFt) : undefined,
    }));

  return {
    acreage: numericFieldToContract(formData.acreage),
    address: formData.address,
    yearBuilt: formData.yearBuilt ? Number(formData.yearBuilt) : undefined,
    bbl: formData.bbl || undefined,
    bin: formData.bin || undefined,
    buildingTypeId: formData.buildingTypeId,
    city: formData.city,
    companyId: companyId,
    espmPropertyTypeCategoryId: formData.espmPropertyTypeCategoryId,
    espmPropertyTypeDetailId: formData.espmPropertyTypeDetailId,
    floors: numericFieldToContract(formData.floors),
    hvacPrimaryTypeIds: formData.hvacPrimaryTypeIds ?? [],
    isWifiAvailable: formData.hasWifi === "Yes",
    landTypeId: formData.land === "Leased" ? 11 : 10,
    mixedUseComponents:
      mixedUseComponents && mixedUseComponents.length > 0
        ? mixedUseComponents
        : undefined,
    paysBill: formData.paysBill,
    projects: [],
    propertyId: propertyId ?? Number(formData.propertyId ?? "0"),
    propertyName: formData.propertyName,
    propertyTypeId: Number(formData.propertyTypeId),
    roofSize: numericFieldToContract(formData.roofSize),
    sqFt: numericFieldToContract(formData.sqft),
    state: formData.state,
    suite: formData.suite,
    units: numericFieldToContract(formData.units),
    utilityIds: formData.utilityIds ?? [],
    priorWorkTypeIds: formData.priorWorkTypeIds ?? [],
    zip: formData.zip,
    electricUtilityProvider: formData.electricUtilityProvider || undefined,
    gasUtilityProvider: formData.gasUtilityProvider || undefined,

    // Deliberately not `|| undefined`: "" and [] are how an owner un-answers,
    // and collapsing them to undefined would make the blank option do nothing.
    ownerClass: formData.ownerClass,
    buildingUses: formData.buildingUses,
    isHistoric: triStateFactToContract(formData.isHistoric),
    isBarn: triStateFactToContract(formData.isBarn),
    isAffordableHousing: triStateFactToContract(formData.isAffordableHousing),
    isIncomeQualified: triStateFactToContract(formData.isIncomeQualified),
    isPrimaryResidence: triStateFactToContract(formData.isPrimaryResidence),
  };
};

/**
 * Maps a property contract (e.g. from a GET) back to property form data. Inverse of
 * mapPropertyFormToContract -- used to seed an edit form (or a disposable QA screening-sandbox
 * form) from a property's currently saved values.
 */
export const mapPropertyContractToFormData = (
  propertyData: IPropertyContract,
  companyId: number
): IPropertyFormDataType => ({
  acreage: propertyData.acreage?.toString() ?? "",
  address: propertyData.address,
  yearBuilt: propertyData.yearBuilt?.toString() ?? "",
  bbl: propertyData.bbl || undefined,
  bin: propertyData.bin || undefined,
  buildingType: "",
  buildingTypeId: propertyData.buildingTypeId,
  city: propertyData.city,
  companyId,
  espmPropertyTypeCategoryId: propertyData.espmPropertyTypeCategoryId ?? undefined,
  espmPropertyTypeDetailId: propertyData.espmPropertyTypeDetailId ?? undefined,
  file: [],
  floors: propertyData.floors ? propertyData.floors.toString() : "",
  hasWifi: propertyData.isWifiAvailable ? "Yes" : "No",
  hvacPrimaryTypeIds: propertyData.hvacPrimaryTypeIds ?? [],
  land: propertyData.landTypeId === 11 ? "Leased" : "Owned",
  mixedUseComponents: propertyData.mixedUseComponents ?? undefined,
  paysBill: propertyData.paysBill?.toString() ?? "",
  priorWorkTypeIds: propertyData.priorWorkTypeIds ?? [],
  propertyId: propertyData.propertyId,
  propertyName: propertyData.propertyName,
  propertyTypeId: Number(propertyData.propertyTypeId),
  roofSize: propertyData.roofSize?.toString() ?? "",
  sqft: propertyData.sqFt?.toString() ?? "",
  state: propertyData.state,
  suite: propertyData.suite,
  units: propertyData.units?.toString() ?? "",
  utilityIds: propertyData.meters
    ? Array.from(new Set(propertyData.meters.map((m) => m.utilityId)))
    : [],
  electricUtilityProvider: propertyData.electricUtilityProvider ?? "",
  gasUtilityProvider: propertyData.gasUtilityProvider ?? "",
  ownerClass: propertyData.ownerClass ?? "",
  buildingUses: propertyData.buildingUses ?? [],
  isHistoric: triStateFactToForm(propertyData.isHistoric),
  isBarn: triStateFactToForm(propertyData.isBarn),
  isAffordableHousing: triStateFactToForm(propertyData.isAffordableHousing),
  isIncomeQualified: triStateFactToForm(propertyData.isIncomeQualified),
  isPrimaryResidence: triStateFactToForm(propertyData.isPrimaryResidence),
  zip: propertyData.zip,
});

/**
 * Uploads property image files after property save
 * @param api - The ZeosApiService instance
 * @param propertyId - The property ID to associate the images with
 * @param files - Array of files to upload
 * @throws Error if upload fails
 */
export const uploadPropertyImages = async (
  api: IZeosApiService,
  propertyId: number,
  files: File[]
): Promise<void> => {
  if (!files || files.length === 0) {
    return;
  }

  try {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      await api.Document.createAsync({
        entityTypeId: EntityTypeEnum.Property,
        entityId: propertyId,
        documentTypeId: resolveDocumentType(file),
        documentCategoryId: DocumentCategoryEnum.PropertyImage,
        file: file,
      });
    }
  } catch (error) {
    console.error("Error uploading property files:", error);
    throw new Error("Failed to upload property files");
  }
};

/**
 * Interface for checkComplianceStatus parameters
 */
export interface IComplianceStatusParams {
  api: IZeosApiService;
  log: ReturnType<typeof createLogger>;
  zip: string;
  bin?: string;
  bbl?: string;
  propertyId: number;
}

/**
 * Interface for checkComplianceStatus return value
 */
export interface IComplianceStatusResult {
  needsBinBbl: boolean;
  offerCarbonClear?: boolean;
  carbonClearStatus: CarbonClearGovEnum;
}

/**
 * Checks compliance status for a property and returns whether BIN/BBL is needed
 * and whether Carbon Clear should be offered
 * @param params - Object containing api, log, zip, bin, bbl, and propertyId
 * @returns Object containing needsBinBbl and offerCarbonClear values
 */
export const checkComplianceStatus = async (
  params: IComplianceStatusParams
): Promise<IComplianceStatusResult> => {
  const { api, log, zip, bin, bbl, propertyId } = params;

  if (!propertyId) {
    return {
      needsBinBbl: false,
      offerCarbonClear: undefined,
      carbonClearStatus: CarbonClearGovEnum.None,
    };
  }

  try {
    const zipResponse = await api.Location.isNycZipCodeAsync(zip);
    const isNycZip = zipResponse.data;

    if (!isNycZip) {
      return {
        needsBinBbl: false,
        offerCarbonClear: undefined,
        carbonClearStatus: CarbonClearGovEnum.None,
      };
    }
  } catch (error) {
    log.error("Error checking NYC zip code:", error);
    // On error, default to not offering Carbon Clear to be safe
    return {
      needsBinBbl: false,
      offerCarbonClear: undefined,
      carbonClearStatus: CarbonClearGovEnum.None,
    };
  }

  const hasBin = bin && bin.length > 0;
  const hasBbl = bbl && bbl.length > 0;

  // Check if property needs BIN/BBL
  if (!hasBin && !hasBbl) {
    return {
      needsBinBbl: true,
      offerCarbonClear: undefined,
      carbonClearStatus: CarbonClearGovEnum.None,
    };
  }

  if (!FeatureFlags.enableCarbonClear) {
    return {
      needsBinBbl: false,
      offerCarbonClear: undefined,
      carbonClearStatus: CarbonClearGovEnum.None,
    };
  }

  // If we have either BIN or BBL, run compliance check
  if (hasBin || hasBbl) {
    const complianceRequest: IAutoComplianceRequest = {
      bin: bin,
      bbl: bbl,
      zipCode: zip,
      propertyId: propertyId,
    };

    try {
      const compliance = await api.AutoCompliance.checkGovernance(
        complianceRequest
      );
      const offerCarbonClear = compliance.data?.ll97;
      let carbonClearStatus = CarbonClearGovEnum.None;

      if (offerCarbonClear) {
        try {
          const firstCheck = await api.AutoCompliance.carbonClearGovernance({
            propertyId,
            type: CarbonClearTestEnum.Check,
          });
          if (firstCheck.data == CarbonClearGovEnum.InProgress) {
            const secondCheck = await api.AutoCompliance.carbonClearGovernance({
              propertyId,
              type: CarbonClearTestEnum.Update,
            });
            carbonClearStatus = secondCheck.data;
          } else {
            carbonClearStatus = firstCheck.data ?? CarbonClearGovEnum.None;
          }
        } catch (statusError) {
          log.error("Error updating Carbon Clear governance:", statusError);
        }
      }

      return {
        needsBinBbl: false,
        offerCarbonClear,
        carbonClearStatus,
      };
    } catch (error) {
      log.error("Error checking compliance:", error);
      return {
        needsBinBbl: false,
        offerCarbonClear: undefined,
        carbonClearStatus: CarbonClearGovEnum.None,
      };
    }
  }

  return {
    needsBinBbl: false,
    offerCarbonClear: undefined,
    carbonClearStatus: CarbonClearGovEnum.None,
  };
};
